// src/pages/UserManagement.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  Admin-only page.
//  - Paginated users table
//  - "Add User" modal with form validation
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Space,
  Typography,
  Card,
  message,
  Tooltip,
  Row,
  Col,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  UserOutlined,
  CrownOutlined,
  ReloadOutlined,
  TeamOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  getUsersApi,
  createUserApi,
  updateUserApi,
  deleteUserApi,
  getRoleOptionsApi,
} from "../api/user.api";
import { useAuth } from "../context/AuthContext";

const { Title, Text } = Typography;
const { Option } = Select;

const UserManagement = () => {
  const { hasPermission, hasAnyPermission } = useAuth();
  const canWrite = hasPermission("users.write");
  const canLoadRoleOptions = hasAnyPermission(["users.read", "users.write"]);

  const [users, setUsers] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [tableLoading, setTableLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  useEffect(() => {
    if (!canLoadRoleOptions) return;
    getRoleOptionsApi()
      .then(({ data }) => setRoleOptions(data.data.roles || []))
      .catch(() => setRoleOptions([]));
  }, [canLoadRoleOptions]);

  const roleSelectOptions = useMemo(() => {
    if (roleOptions.length > 0) {
      return roleOptions.map((r) => (
        <Option key={r.id} value={r.name}>
          <Space>
            {r.name === "admin" ? <CrownOutlined /> : <UserOutlined />}{" "}
            <span style={{ textTransform: "capitalize" }}>{r.name}</span>
          </Space>
        </Option>
      ));
    }
    return [
      <Option key="user" value="user">
        <Space>
          <UserOutlined /> User
        </Space>
      </Option>,
      <Option key="admin" value="admin">
        <Space>
          <CrownOutlined /> Admin
        </Space>
      </Option>,
    ];
  }, [roleOptions]);

  // ── Fetch users ────────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async (page = 1, limit = 10, q = "") => {
    setTableLoading(true);
    try {
      const { data } = await getUsersApi(page, limit, q);
      setUsers(data.data.users);
      setPagination((prev) => ({
        ...prev,
        current: data.data.pagination.page,
        total: data.data.pagination.total,
        pageSize: data.data.pagination.limit,
      }));
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to load users.");
    } finally {
      setTableLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers(1, pagination.pageSize, searchText);
  }, [fetchUsers, pagination.pageSize, searchText]);

  // ── Create user ────────────────────────────────────────────────────────────
  const handleCreate = async (values) => {
    setCreateLoading(true);
    try {
      await createUserApi(values);
      message.success(`User "${values.name}" created successfully!`);
      setCreateModalOpen(false);
      createForm.resetFields();
      fetchUsers(pagination.current, pagination.pageSize, searchText);
    } catch (err) {
      const backendErrors = err.response?.data?.errors;
      if (backendErrors?.length) {
        createForm.setFields(
          backendErrors.map((e) => ({ name: e.field, errors: [e.message] })),
        );
      } else {
        message.error(err.response?.data?.message || "Failed to create user.");
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditOpen = (user) => {
    setSelectedUser(user);
    editForm.setFieldsValue({
      name: user.name,
      email: user.email,
      role: user.role,
      is_active: Boolean(user.is_active),
    });
    setEditModalOpen(true);
  };

  const handleUpdate = async (values) => {
    if (!selectedUser?.id) return;
    setEditLoading(true);
    try {
      await updateUserApi(selectedUser.id, values);
      message.success(`User "${values.name}" updated successfully!`);
      setEditModalOpen(false);
      setSelectedUser(null);
      editForm.resetFields();
      fetchUsers(pagination.current, pagination.pageSize, searchText);
    } catch (err) {
      const backendErrors = err.response?.data?.errors;
      if (backendErrors?.length) {
        editForm.setFields(
          backendErrors.map((e) => ({ name: e.field, errors: [e.message] })),
        );
      } else {
        message.error(err.response?.data?.message || "Failed to update user.");
      }
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (user) => {
    try {
      await deleteUserApi(user.id);
      message.success(`User "${user.name}" deleted successfully!`);
      fetchUsers(pagination.current, pagination.pageSize, searchText);
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to delete user.");
    }
  };

  const handleSearch = (value) => {
    const next = value.trim();
    setSearchText(next);
    fetchUsers(1, pagination.pageSize, next);
  };

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = [
    {
      title: "ID",
      key: "id",
      dataIndex: "id",
      width: 80,
      align: "center",
    },
    {
      title: "User",
      key: "user",
      render: (_, record) => (
        <Space>
          <span>
            <Text strong style={{ color: "black", display: "block" }}>
              {record.name}
            </Text>
            <Text style={{ color: "black", fontSize: 12 }}>{record.email}</Text>
          </span>
        </Space>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 110,
      render: (role) => (
        <Tag
          color={role === "admin" ? "volcano" : "geekblue"}
          style={{ borderRadius: 20, textTransform: "capitalize" }}
        >
          {role}
        </Tag>
      ),
      filters: [
        { text: "Admin", value: "admin" },
        { text: "User", value: "user" },
      ],
      onFilter: (value, record) => record.role === value,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      render: (active) => (
        <Tag
          color={active ? "success" : "default"}
          style={{ borderRadius: 20 }}
        >
          {active ? "Active" : "Disabled"}
        </Tag>
      ),
    },
    {
      title: "Joined",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      render: (date) => (
        <Text style={{ color: "black", fontSize: 12 }}>
          {new Date(date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </Text>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 140,
      render: (_, record) =>
        canWrite ? (
          <Space>
            <Tooltip title="Edit user">
              <Button
                icon={<EditOutlined />}
                onClick={() => handleEditOpen(record)}
                style={styles.ghostBtn}
              />
            </Tooltip>
            <Popconfirm
              title="Delete user?"
              description={`Disable ${record.name}?`}
              okText="Delete"
              cancelText="Cancel"
              onConfirm={() => handleDelete(record)}
            >
              <Button danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  return (
    <div>
      {/* ── Header ── */}
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 ,padding:"10px 10px"}}>
        <Col>
          <Space align="center">
            <TeamOutlined style={{ fontSize: 22, color: "black" }} />
            <Title level={4} style={{ color: "black", margin: 0 }}>
              User Management
            </Title>
            <Tag color="blue" style={{ borderRadius: 20 }}>
              {pagination.total} total
            </Tag>
          </Space>
        </Col>
        <Col>
          <Space>
            <Tooltip title="Refresh">
              <Button
                icon={<ReloadOutlined />}
                onClick={() =>
                  fetchUsers(
                    pagination.current,
                    pagination.pageSize,
                    searchText,
                  )
                }
                style={styles.ghostBtn}
              />
            </Tooltip>
            <Input.Search
              allowClear
              placeholder="Search by name/email"
              onSearch={handleSearch}
              onChange={(e) => {
                if (!e.target.value) {
                  handleSearch("");
                }
              }}
              enterButton={<SearchOutlined />}
              style={{ width: 240 }}
            />
            {canWrite && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateModalOpen(true)}
                style={styles.primaryBtn}
              >
                Add User
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      {/* ── Table ── */}
      <Card style={styles.tableCard} bodyStyle={{ padding: 0 }}>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={tableLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            onChange: (page, pageSize) =>
              fetchUsers(page, pageSize, searchText),
            style: { padding: "16px 24px" },
          }}
          style={{ background: "transparent" }}
          scroll={{ x: 600 }}
        />
      </Card>

      {/* ── Add User Modal ── */}
      <Modal
        title={
          <Text strong style={{ color: "black", fontSize: 16 }}>
            <PlusOutlined style={{ color: "black", marginRight: 8 }} />
            Create New User
          </Text>
        }
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          createForm.resetFields();
        }}
        footer={null}
        styles={{
          content: { background: "#ffffff", borderRadius: 12 },
          header: { background: "#ffffff", borderBottom: "1px solid #334155" },
          mask: { backdropFilter: "blur(4px)" },
        }}
        width={480}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreate}
          requiredMark={false}
          style={{ marginTop: 20 }}
        >
          <Form.Item
            label={<Text style={{ color: "black" }}>Full Name</Text>}
            name="name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input
              placeholder="John Doe"
              prefix={<UserOutlined style={{ color: "black" }} />}
              style={styles.modalInput}
            />
          </Form.Item>

          <Form.Item
            label={<Text style={{ color: "#94a3b8" }}>Email Address</Text>}
            name="email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Enter a valid email" },
            ]}
          >
            <Input
              placeholder="john@example.com"
              style={styles.modalInput}
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item
            label={<Text style={{ color: "#94a3b8" }}>Password</Text>}
            name="password"
            rules={[
              { required: true, message: "Password is required" },
              { min: 8, message: "Minimum 8 characters" },
              {
                pattern: /[A-Z]/,
                message: "Must contain at least one uppercase letter",
              },
              {
                pattern: /[0-9]/,
                message: "Must contain at least one number",
              },
            ]}
          >
            <Input.Password
              placeholder="Min 8 chars, 1 uppercase, 1 number"
              style={styles.modalInput}
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            label={<Text style={{ color: "#94a3b8" }}>Role</Text>}
            name="role"
            initialValue="user"
          >
            <Select style={{ ...styles.modalInput }}>{roleSelectOptions}</Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setCreateModalOpen(false);
                  createForm.resetFields();
                }}
                style={styles.ghostBtn}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createLoading}
                style={styles.primaryBtn}
              >
                Create User
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Edit User Modal ── */}
      <Modal
        title={
          <Text strong style={{ color: "black", fontSize: 16 }}>
            <EditOutlined style={{ color: "black", marginRight: 8 }} />
            Edit User
          </Text>
        }
        open={editModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          setSelectedUser(null);
          editForm.resetFields();
        }}
        footer={null}
        styles={{
          content: { background: "#ffffff", borderRadius: 12 },
          header: { background: "#ffffff", borderBottom: "1px solid #334155" },
          mask: { backdropFilter: "blur(4px)" },
        }}
        width={480}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdate}
          requiredMark={false}
          style={{ marginTop: 20 }}
        >
          <Form.Item
            label={<Text style={{ color: "black" }}>Full Name</Text>}
            name="name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input style={styles.modalInput} />
          </Form.Item>

          <Form.Item
            label={<Text style={{ color: "#94a3b8" }}>Email Address</Text>}
            name="email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Enter a valid email" },
            ]}
          >
            <Input style={styles.modalInput} autoComplete="off" />
          </Form.Item>

          <Form.Item
            label={<Text style={{ color: "#94a3b8" }}>Role</Text>}
            name="role"
            rules={[{ required: true, message: "Role is required" }]}
          >
            <Select style={{ ...styles.modalInput }}>{roleSelectOptions}</Select>
          </Form.Item>

          <Form.Item
            label={<Text style={{ color: "#94a3b8" }}>Status</Text>}
            name="is_active"
          >
            <Select style={{ ...styles.modalInput }}>
              <Option value={true}>Active</Option>
              <Option value={false}>Disabled</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setEditModalOpen(false);
                  setSelectedUser(null);
                  editForm.resetFields();
                }}
                style={styles.ghostBtn}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={editLoading}
                style={styles.primaryBtn}
              >
                Update User
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  tableCard: {
    background: "white",
    border: "1px solid #334155",
    borderRadius: 12,
    overflow: "hidden",
  },
  modalInput: {
    background: "white",
    border: "1px solid #334155",
    borderRadius: 8,
    color: "black",
  },
  primaryBtn: {
    background: "rgb(120, 57, 120)",
    border: "none",
    borderRadius: 8,
  },
  ghostBtn: {
    background: "transparent",
    border: "1px solid #334155",
    color: "#94a3b8",
    borderRadius: 8,
  },
};

export default UserManagement;
